import { AdminWorkspacesCleanupDto } from '@shared/dto/AdminWorkspacesCleanupDto';
import { AdminImportModulesDto } from '@shared/dto/AdminImportModulesDto';
import { AdminTriggerWorkspaceRefreshPlansDto } from '@shared/dto/AdminTriggerWorkspaceRefreshPlansDto';
import { CreateModuleDto } from '@shared/dto/CreateModuleDto';
import { GetModuleQueryParams, GetModuleRouteParams } from '@shared/dto/PrepareModuleDto';
import { UpdateModuleDto } from '@shared/dto/UpdateModuleDto';
import { UpdateModulesConfigDto } from '@shared/dto/UpdateModulesConfigDto';
import { ApiAzureDevOpsPipeline, ApiAzureDevOpsProcess, ApiAzureDevOpsProject, ApiAzureLocation, ApiAzureResourceGroup, ApiAzureSubscription, ApiCheckDeleteStep, ApiCheckDeleteStepResult, ApiCleanupEmptyWorkspaceRepositoriesResult, ApiCleanupEmptyWorkspacesResult, ApiCustomProjectData, ApiDelineaFolderExistsResult, ApiDelineaFolderLookupItem, ApiDelineaPlatformUser, ApiMissingDeleteRequestRepositoriesResult, ApiMissingDeleteRequestsResult, ApiModuleData, ApiModulesConfig, ApiResource, ApiResourceFull, ApiServiceNowBusinessService, ApiServiceNowUserGroup, ApiStatus, ApiTriggerWorkspaceRefreshPlansResult, ApiVSphereCluster, ApiVSphereDatastore, ApiVSphereDatastoreCluster, ApiVSphereFolder, ApiVSphereHost, ApiVSphereNetwork, ApiVSphereTagCategories, ApiVSphereTags } from '@shared/interfaces/ApiInterfaces';
import { ModuleConfiguration } from '@shared/interfaces/CommonInterfaces';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import axios, { AxiosError } from 'axios';
import { msalInstance } from '../authConfig';
import { parseDateInterceptor } from '../shared/util/AxiosInterceptors';
import { MultiError } from '../utils/errors';
import { setAccessToken as setInMemoryAccessToken } from '../utils/accessTokenStore';

axios.interceptors.response.use(parseDateInterceptor);

const AUTH_REDIRECT_IN_PROGRESS_KEY = 'authRedirectInProgress';

class BackendApiService {
  private baseUrl = process.env.REACT_APP_BACKEND_API_URL;
  private token: string;

  // REMARK: authentication is checked via OIDC token, code is only required to protect the public /health check 
  private codeParam = process.env.REACT_APP_ENVIRONMENT !== 'local' ? `?code=${process.env.REACT_APP_FA_CODE}` : ""

  constructor(token?: string) {
    this.token = token ?? '';
  }

  public setToken(token: string) {
    this.token = token;
  }

  private persistRequestedUrlForReauth() {
    const currentUrl = window.location.pathname + window.location.search;
    const storedUrl = sessionStorage.getItem('requestedUrl');

    // Never overwrite a deep-link with root. Keep the most specific return target.
    if (storedUrl && storedUrl !== '/') {
      return;
    }

    if (currentUrl && currentUrl !== '/') {
      sessionStorage.setItem('requestedUrl', currentUrl);
    }
  }

  private async refreshAccessToken(): Promise<boolean> {
    try {
      const account = msalInstance.getAllAccounts()[0];
      if (!account) {
        return false;
      }

      const response = await msalInstance.acquireTokenSilent({
        scopes: [`${process.env.REACT_APP_IDENTIFIER_URI}/.default`],
        account,
      });

      this.token = response.accessToken;
      setInMemoryAccessToken(response.accessToken);
      return true;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        this.persistRequestedUrlForReauth();
        sessionStorage.setItem(AUTH_REDIRECT_IN_PROGRESS_KEY, 'true');
        await msalInstance.loginRedirect({
          scopes: [`${process.env.REACT_APP_IDENTIFIER_URI}/.default`],
        });
      }
      return false;
    }
  }

  private async executeWithAuthRetry<T>(requestFn: () => Promise<T>, hasRetried: boolean = false): Promise<T> {
    try {
      return await requestFn();
    } catch (error: any) {
      const isUnauthorized = error?.response?.status === 401;
      if (isUnauthorized && !hasRetried) {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          return await this.executeWithAuthRetry(requestFn, true);
        }
      }
      throw error;
    }
  }

  private async httpGet(path: string, queryParams: any = {}): Promise<any> {
    try {
      const response = await this.executeWithAuthRetry(() => axios.get<any>(
        `${this.baseUrl}/${path}${this.codeParam}`,
        {
          headers: { "Authorization": `Bearer ${this.token}` },
          params: queryParams
        }
      ));
      if (response.data.value) {
        return response.data.value;
      }
      return response.data;
    } catch (error: any) {
      this.handleApiError(error);
    }
  }

  private async httpPost(path: string, requestBody: any): Promise<any> {
    try {
      const response = await this.executeWithAuthRetry(() => axios.post<any>(
        `${this.baseUrl}/${path}${this.codeParam}`, requestBody,
        { headers: { "Authorization": `Bearer ${this.token}` } }
      ));
      if (response.data.value) {
        return response.data.value;
      }
      return response.data;
    } catch (error: any) {
      this.handleApiError(error);
    }
  }

  private async httpPut(path: string, requestBody: any): Promise<any> {
    try {
      const response = await this.executeWithAuthRetry(() => axios.put<any>(
        `${this.baseUrl}/${path}${this.codeParam}`, requestBody,
        { headers: { "Authorization": `Bearer ${this.token}` } }
      ));
      if (response.data.value) {
        return response.data.value;
      }
      return response.data;
    } catch (error: any) {
      this.handleApiError(error);
    }
  }

  private async httpDelete(path: string, queryParams: any = {}): Promise<any> {
    try {
      const response = await this.executeWithAuthRetry(() => axios.delete<any>(
        `${this.baseUrl}/${path}${this.codeParam}`,
        {
          headers: { "Authorization": `Bearer ${this.token}` },
          params: queryParams
        }
      ));
      if (response.data.value) {
        return response.data.value;
      }
      return response.data;
    } catch (error: any) {
      this.handleApiError(error);
    }
  }

  private handleApiError(error: unknown) {
    console.error(error);
    if (error instanceof AxiosError) {
      const errorData = error.response?.data;
      if (errorData?.errors) {
        console.log("multi errorData", errorData.errors[0]);
        throw new MultiError(errorData.errors.map((e: any) => e.title));
      } else {
        console.log("errorData", errorData);
        throw new Error(errorData?.message);
      }
    } else {
      throw new Error("Unknown error: " + error);
    }
  }

  public async authorized(): Promise<boolean> {
    try {
      const result = await this.httpGet("authorized");
      return result.authorized;
    } catch (err) {
      return false;
    }
  }

  public async getMaintenanceConfig(): Promise<{ enabled: boolean; title: string; description: string }> {
    try {
      const result = await this.httpGet("maintenance");
      return result;
    } catch (err) {
      return { enabled: false, title: '', description: '' };
    }
  }

  public async getModules(): Promise<ModuleConfiguration[]> {
    const result = await this.httpGet("modules");
    return result;
  }

  public async prepareModuleRequest(prepareModuleData: GetModuleRouteParams, prepareModuleQueries: GetModuleQueryParams): Promise<ApiModuleData> {
    const result = await this.httpGet(`modules/${prepareModuleData.platform}/${prepareModuleData.name}`, prepareModuleQueries);
    return result;
  }

  public async createModuleRequest(createModuleData: CreateModuleDto, options?: { dryRun?: boolean }): Promise<ApiStatus> {
    const result = await this.httpPost("modules", {
      ...createModuleData,
      ...(options?.dryRun !== undefined && { dryRun: options.dryRun })
    });
    return result;
  }

  public async updateModuleRequest(platform: string, name: string, requestId: string, updateModuleData: UpdateModuleDto, options?: { dryRun?: boolean }): Promise<ApiStatus> {
    const result = await this.httpPut(`modules/${platform}/${name}/${requestId}`, {
      ...updateModuleData,
      ...(options?.dryRun !== undefined && { dryRun: options.dryRun })
    });
    return result;
  }

  public async checkDeleteStep(platform: string, name: string, requestId: string, moduleId: string, step: ApiCheckDeleteStep): Promise<ApiCheckDeleteStepResult> {
    const result = await this.httpGet(`modules/${platform}/${name}/${requestId}`, { moduleId, action: "check-delete", step });
    return result;
  }

  public async deleteModuleRequest(
    platform: string,
    name: string,
    requestId: string,
    moduleId: string,
    options?: { markAsInvalid?: boolean; invalidationReason?: string }
  ): Promise<ApiStatus> {
    const result = await this.httpDelete(`modules/${platform}/${name}/${requestId}`, {
      moduleId,
      markAsInvalid: options?.markAsInvalid,
      invalidationReason: options?.invalidationReason
    });
    return result;
  }

  public async cancelModuleRequest(platform: string, name: string, requestId: string): Promise<ApiStatus> {
    const result = await this.httpDelete(`modules/${platform}/${name}/${requestId}`, { action: "cancel" });
    return result;
  }

  public async getCustomProjects(): Promise<ApiCustomProjectData[]> {
    const result = await this.httpGet("customprojects");
    return result;
  }

  public async getServiceNowBusinessServices(): Promise<ApiServiceNowBusinessService[]> {
    const result = await this.httpGet("servicenow/businessservices");
    return result;
  }

  public async getServiceNowUserGroups(properties: any): Promise<ApiServiceNowUserGroup[]> {
    const result = await this.httpGet("servicenow/usergroups", properties);
    return result;
  }

  public async getUserRequests(
    status?: string,
    sinceDays?: number,
    showAll: boolean = false,
    includeCancelled: boolean = true,
    includeDeleted: boolean = true,
    includeParents?: boolean,
    windowed?: boolean
  ): Promise<ApiResource[]> {
    const params: any = { status, sinceDays, showAll, includeCancelled, includeDeleted };
    if (includeParents !== undefined) {
      params.includeParents = includeParents;
    }
    if (windowed !== undefined) {
      params.windowed = windowed;
    }
    const result = await this.httpGet("userrequests", params);
    return result;
  }

  public async getUserRequest(id?: string): Promise<ApiResourceFull> {
    const result = await this.httpGet(`userrequests/${id}`);
    return result;
  }

  public async getUserRequestParentDetails(platform?: string, name?: string, resourceName?: string): Promise<ApiResource | null> {
    const result = await this.httpGet("userrequests", { lookupPlatform: platform, lookupName: name, lookupResourceName: resourceName });
    return result;
  }

  public async getServicePrincipals(properties: any): Promise<any> {
    const result = await this.httpGet(`azure/entra/serviceprincipals`, properties);
    return result;
  }

  public async getEntraGroups(properties: any): Promise<any> {
    const result = await this.httpGet(`azure/entra/groups`, properties);
    return result;
  }

  public async getEntraUsers(properties: any): Promise<any> {
    const result = await this.httpGet(`azure/entra/users`, properties);
    return result;
  }

  public async getDelineaPlatformUsers(properties: any): Promise<ApiDelineaPlatformUser[]> {
    const result = await this.httpGet(`delinea/platformusers`, properties);
    return result;
  }

  public async getAzureDevOpsProjects(properties: any): Promise<ApiAzureDevOpsProject[]> {
    const result = await this.httpGet(`azuredevops/projects`, properties);
    return result;
  }

  public async getAzureDevOpsProcesses(properties: any): Promise<ApiAzureDevOpsProcess[]> {
    const result = await this.httpGet(`azuredevops/processes`, properties);
    return result;
  }

  public async getAzureDevOpsPipelines(properties: any): Promise<ApiAzureDevOpsPipeline[]> {
    const result = await this.httpGet(`azuredevops/pipelines`, properties);
    return result;
  }

  public async getDelineaFoldersLookup(properties: any): Promise<ApiDelineaFolderLookupItem[]> {
    const result = await this.httpGet(`delinea/folders/lookup`, properties);
    return result;
  }

  public async checkDelineaFolderAlreadyExists(properties: any): Promise<ApiDelineaFolderExistsResult> {
    const result = await this.httpGet(`delinea/folders/exists`, properties);
    return result;
  }

  public async getAzureSubscriptions(properties: any): Promise<ApiAzureSubscription[]> {
    const result = await this.httpGet("azure/subscriptions", properties);
    return result;
  }

  public async getAzureLocations(properties: any): Promise<ApiAzureLocation[]> {
    const result = await this.httpGet("azure/locations", properties);
    return result;
  }

  public async getAzureResourceGroups(properties: any): Promise<ApiAzureResourceGroup[]> {
    const result = await this.httpGet("azure/resources/resourcegroups", properties );
    return result;
  }

  public async getModulesConfig(): Promise<ApiModulesConfig[]> {
    const result = await this.httpGet("modules-config");
    return result;
  }

  public async updateModulesConfig(updateModulesConfig: UpdateModulesConfigDto): Promise<ApiModulesConfig> {
    const result = await this.httpPut(`modules-config`, updateModulesConfig);
    return result;
  }

  public async importModules(importModulesData: AdminImportModulesDto): Promise<{
    [key: string]: string[];
  }> {
    const result = await this.httpPost(`import-modules`, importModulesData);
    return result;
  }

  public async cleanupEmptyWorkspaces(inputData: AdminWorkspacesCleanupDto): Promise<ApiCleanupEmptyWorkspacesResult> {
    const result = await this.httpPost(`cleanup-empty-workspaces`, inputData);
    return result;
  }

  public async getCleanupEmptyWorkspaceRepositories(inputData: AdminWorkspacesCleanupDto): Promise<ApiCleanupEmptyWorkspaceRepositoriesResult> {
    const result = await this.httpPost(`cleanup-empty-workspaces/repositories`, inputData);
    return result;
  }

  public async triggerWorkspaceRefreshPlans(inputData: AdminTriggerWorkspaceRefreshPlansDto): Promise<ApiTriggerWorkspaceRefreshPlansResult> {
    const result = await this.httpPost(`cleanup-empty-workspaces/refresh-plans`, inputData);
    return result;
  }

  public async reconcileMissingDeleteRequests(inputData: AdminWorkspacesCleanupDto): Promise<ApiMissingDeleteRequestsResult> {
    const result = await this.httpPost(`reconcile-missing-delete-requests`, inputData);
    return result;
  }

  public async getReconcileMissingDeleteRequestRepositories(inputData: AdminWorkspacesCleanupDto): Promise<ApiMissingDeleteRequestRepositoriesResult> {
    const result = await this.httpPost(`reconcile-missing-delete-requests/repositories`, inputData);
    return result;
  }

  public async getVSphereDatacenters(properties: any): Promise<any> {
    const result = await this.httpGet(`vsphere/datacenters`, properties);
    return result;
  }

  public async getVSphereClusters(properties: any): Promise<ApiVSphereCluster[]> {
    const result = await this.httpGet("vsphere/clusters", properties);
    return result;
  }

  public async getVSphereHosts(properties: any): Promise<ApiVSphereHost[]> {
    const result = await this.httpGet("vsphere/hosts", properties);
    return result;
  }

  public async getVSphereFolders(properties: any): Promise<ApiVSphereFolder[]> {
    const result = await this.httpGet("vsphere/folders", properties);
    return result;
  }

  public async getVSphereTagCategories(properties: any): Promise<ApiVSphereTagCategories[]> {
    const result = await this.httpGet("vsphere/tagcategories", properties);
    return result;
  }

  public async getVSphereTags(properties: any): Promise<ApiVSphereTags[]> {
    const result = await this.httpGet("vsphere/tags", properties);
    return result;
  }

  public async getVSphereDatastores(properties: any): Promise<ApiVSphereDatastore[]> {
    return await this.httpGet("vsphere/datastores", properties);
  }

  public async getVSphereDatastoreClusters(properties: any): Promise<ApiVSphereDatastoreCluster[]> {
    return await this.httpGet("vsphere/datastoreclusters", properties);
  }

  public async getVSphereNetworks(properties: any): Promise<ApiVSphereNetwork[]> {
    const result = await this.httpGet("vsphere/networks", properties);
    return result;
  }

  public async getVitalQIPAddressLookup(properties: any): Promise<any> {
    const result = await this.httpGet("vitalqip/address", properties);
    return result;
  }

  public async triggerPipelineAction(
    moduleName: string,
    platform: string,
    actionName: string,
    pullRequestId: number,
    moduleData?: Record<string, any>,
    userParameters?: Record<string, string>
  ): Promise<any> {
    const result = await this.httpPost("pipeline-actions/trigger", {
      moduleName,
      platform,
      actionName,
      pullRequestId,
      moduleData,
      userParameters
    });
    return result;
  }

  public async getPipelineStatus(pipelineId: string, runId: string): Promise<any> {
    const result = await this.httpGet(`pipeline-actions/status/${pipelineId}/${runId}`);
    return result;
  }

  public async getAzureSnapshots(vmName: string, region?: string, moduleName?: string, platform?: string): Promise<string[]> {
    const result = await this.httpGet("azure/resources/snapshots", { vmName, region, moduleName, platform });
    return result;
  }
}

export default BackendApiService;